import { ReactElement, useContext, useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AdaButton, Divider, Loading } from '@betrybe/ada-react';
import * as Sentry from '@sentry/browser';
import { FocusError } from 'focus-formik-error';

import Alert from '@/components/Alert';
import { StudentContext, useToastNotification } from '@/contexts';
import { DefaultLayout } from '@/layouts/index';
import { checkErrors, mountGuarantorObj } from '@/utils';
import getAddress from '@/services/cep';
import type { getGuarantorsType } from '@/services/pre-enrollment/types';
import { useClient, useStateMachine } from '@/hooks';
import useLazyTrack from '@/hooks/tracking/useLazyTrack';
import { ActorTypeEnum, SourceEnum, VerbEnum } from '@/tracker/enums';
import StatesEnum from '@/utils/enums/StatesEnum';
import LayoutDescription from '../components';
import fileUpload from '@/utils/file-upload-function/fileUpload';
import useGuarantor from '@/services/pre-enrollment/useGuarantor';
import BasicInformationForm from '../form-components/basic-information-form';
import AddressForm from '../form-components/address-form';
import DocumentsForm from '../form-components/documents-form';
import AdditionalInformationForm from '../form-components/additional-information-form';
import {
  guarantorFormDescriptionV3,
  guarantorFormEndDescription,
  guarantorFormTitleV3,
} from './constants/textsV3';
import { firstGuarantorTitle, secondGuarantorTitle } from './constants/textsV2';
import {
  catchApiResponse,
  guarantorsInitialFormValues,
  guarantosFormValidationSchema,
} from './utils';

function GuarantorsForm(): ReactElement {
  const history = useHistory();
  const {
    clientId,
    currentState,
    selectionProcessId,
    processInfo,
    processDateInfo,
    stateMachineVersion,
    setActualState,
  } = useClient();
  const { getStateObject } = useStateMachine();
  const [loading, setLoading] = useState(false);
  const degreeOfRelatednessRef = useRef<null | HTMLDivElement>(null);
  const nationalityRef = useRef<null | HTMLDivElement>(null);
  const cpfFileRef = useRef<null | HTMLDivElement>(null);
  const rgFileRef = useRef<null | HTMLDivElement>(null);
  const proofOfAddressRef = useRef<null | HTMLDivElement>(null);
  const selfieWithDocumentRef = useRef<null | HTMLDivElement>(null);
  const { state, setState } = useContext(StudentContext);
  const [firstGuarantor, setFirstGuarantor] = useState<getGuarantorsType>();
  const track = useLazyTrack();
  const [, toastNotificationDispatch] = useToastNotification();
  const { updateGuarantor, createGuarantor, getGuarantors } = useGuarantor();

  const [proofOfAddressImageURL, setProofOfAddressImageURL] =
    useState<string>('');
  const [cpfFileImageURL, setCpfFileImageURL] = useState<string>('');
  const [rgFileImageURL, setRgFileImageURL] = useState<string>('');
  const [selfieImageURL, setSelfieImageURL] = useState<string>('');

  const [documentsApiError, setDocumentsApiError] = useState<string>('');
  const [basicInformationApiError, setBasicInformationApiError] =
    useState<boolean>(false);

  const isFirstGuarantor =
    history?.location?.pathname === '/register/first-person-guarantor' ||
    history?.location?.pathname === '/register/guarantor-form';
  const firstPersonGuarantor = state?.guarantorsPerson[0];
  const secondPersonGuarantor = state?.guarantorsPerson[1];
  const currentGuarantor = isFirstGuarantor
    ? firstPersonGuarantor
    : secondPersonGuarantor;
  const isV3 = stateMachineVersion === 'V3';
  const pageTitleV2 = isFirstGuarantor
    ? firstGuarantorTitle
    : secondGuarantorTitle;
  const documentOwner = isFirstGuarantor
    ? 'first_guarantor'
    : 'second_guarantor';

  const getGuarantorsService = async () => {
    setLoading(true);
    getGuarantors(clientId)
      .then((res) => {
        const { data } = res;
        setState({
          ...state,
          guarantorsPerson: data,
        });
        setFirstGuarantor(data[0]);
      })
      .catch((error) => {
        setLoading(false);
        const payload = {
          userId: clientId,
          currentState,
        };
        Sentry.setUser({ id: clientId });
        Sentry.captureException({ error, payload });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (
      currentState === StatesEnum.SECOND_GUARANTOR_REGISTRATION ||
      currentState === StatesEnum.SECOND_GUARANTOR_REGISTRATION_FORM
    ) {
      getGuarantorsService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.guarantorsPerson.length > 0) {
      setFirstGuarantor(state.guarantorsPerson[0]);
    }
  }, [state.guarantorsPerson]);

  const callTrack = (
    eventName: string,
    component: string,
    guarantor?: getGuarantorsType,
  ) => {
    track(
      eventName,
      {
        actor: {
          user_id: clientId,
          type: ActorTypeEnum.CANDIDATE,
          role: '',
        },
        object: {
          path: window.location.pathname,
          url: window.location.href,
          component,
          params: {
            ...guarantor,
            selection_process_id: selectionProcessId,
          },
          process_info: processInfo,
        },
        context: {
          source: SourceEnum.FINANCING_REQUEST,
        },
        verb: VerbEnum.CLICK,
      },
      'track',
    );
  };

  const setGuarantorSuccessfully = async (guarantor: getGuarantorsType) => {
    callTrack('Guarantor Submitted', 'Cadastrar avalista', guarantor);

    const stateObj = getStateObject({ state: currentState });

    if (stateObj && stateObj.nextState) {
      await setActualState(stateObj.nextState);
      history?.push(stateObj.nextUrl);
      window.scroll(0, 0);
    } else {
      history?.push('/register/guarantors');
    }
  };

  const formik = useFormik({
    initialValues: guarantorsInitialFormValues(currentGuarantor),
    validationSchema: Yup.object().shape(guarantosFormValidationSchema),
    onSubmit: async (values) => {
      const guarantor: getGuarantorsType = await mountGuarantorObj({
        ...values,
        birthdate: new Date(`${values.year}-${values.month}-${values.day}`)
          .toISOString()
          .substring(0, 10),
        cpf_document_url: cpfFileImageURL,
        document_selfie_url: selfieImageURL,
        nationality:
          values.nationality !== 'brasileira'
            ? values.nationalityOther
            : values.nationality,
        politically_exposed_person: values.pep === 'true',
        proof_of_residency_document_url: proofOfAddressImageURL,
        rg_document_url: rgFileImageURL,
        degree_of_relatedness: values.degree_of_relatedness,
        degree_of_relatedness_other:
          values.degree_of_relatedness === 'other'
            ? values.degree_of_relatedness_other
            : null,
        marital_regime:
          values.marital_status !== 'married' ? '' : values.marital_regime,
      });

      const registerGuarantor = () => {
        return currentGuarantor
          ? updateGuarantor(
              clientId,
              currentGuarantor.id,
              selectionProcessId,
              guarantor,
            )
              .then(async () => {
                await setState({
                  ...state,
                  guarantorsPerson: [],
                });
                callTrack(
                  'Guarantor Submitted',
                  'Cadastrar avalista',
                  guarantor,
                );
                history?.push('/register/guarantors');
              })
              .catch((err) =>
                catchApiResponse({
                  err,
                  toastNotificationDispatch,
                  setBasicInformationApiError,
                  setDocumentsApiError,
                  currentState,
                  clientId,
                }),
              )
          : createGuarantor(clientId, selectionProcessId, guarantor)
              .then(async () => {
                formik.resetForm();
                setGuarantorSuccessfully(guarantor);
              })
              .catch((err) =>
                catchApiResponse({
                  err,
                  toastNotificationDispatch,
                  setBasicInformationApiError,
                  setDocumentsApiError,
                  currentState,
                  clientId,
                }),
              );
      };
      return registerGuarantor();
    },
    enableReinitialize: true,
    validateOnChange: false,
  });

  async function handleSearchCep(cep: string) {
    const address = await getAddress(cep);
    formik.setValues((v) => ({
      ...v,
      zipcode: address.cep,
      city: address.localidade,
      address: address.logradouro,
      state: address.uf,
      neighborhood: address.bairro,
    }));
  }

  if (loading) {
    return (
      <DefaultLayout cardSize="normal-card">
        <Loading> Carregando... </Loading>
      </DefaultLayout>
    );
  }

  const isFirstOrSecondGuarantorState =
    currentState !== StatesEnum.FIRST_GUARANTOR_REGISTRATION &&
    currentState !== StatesEnum.SECOND_GUARANTOR_REGISTRATION;

  return (
    <DefaultLayout
      step="2 de 4"
      title={
        (isV3 || state.paymentMethod === 'fifteen_delay') &&
        isFirstOrSecondGuarantorState
          ? guarantorFormTitleV3
          : pageTitleV2
      }
      description={
        <>
          {(isV3 || state.paymentMethod === 'fifteen_delay') && (
            <div className="guarantorForm-v3-layout">
              <p>{guarantorFormDescriptionV3}</p>
              <p className="guarantorForm-v3-layout--description">
                {guarantorFormEndDescription}
              </p>

              <Divider
                position="horizontal"
                className="guarantorForm-v3-layout--divider"
              />
            </div>
          )}

          <LayoutDescription
            text="Lembre-se de ter em mãos os documentos digitalizados da pessoa: RG, CPF, comprovante de endereço e selfie com documento."
            color="primary"
          />

          <Alert />
        </>
      }
      progressPercentage={50}
      limitDate={
        processDateInfo?.limitDateFinancingRequest ||
        state?.processDateInfo.limitDateFinancingRequest
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <FocusError formik={formik} />

        <div className="flex flex-col my-10">
          <BasicInformationForm
            formik={formik}
            nationalityRef={nationalityRef}
            formType="guarantor"
            apiError={basicInformationApiError}
            setApiError={setBasicInformationApiError}
          />
          <AddressForm
            formik={formik}
            // eslint-disable-next-line react/jsx-no-bind
            handleSearchCep={handleSearchCep}
            handleFileUpload={(event, field, fieldName) => {
              fileUpload({
                event,
                field,
                fieldName,
                formik,
                documentOwner,
                clientId,
                setProofOfAddressImageURL,
                setCpfFileImageURL,
                setRgFileImageURL,
                setSelfieImageURL,
              });
            }}
            proofOfAddressRef={proofOfAddressRef}
            formType="guarantor"
            firstGuarantor={firstGuarantor}
            getGuarantorsService={getGuarantorsService}
            callTrack={callTrack}
          />
          <DocumentsForm
            formik={formik}
            cpfFileRef={cpfFileRef}
            rgFileRef={rgFileRef}
            selfieWithDocumentRef={selfieWithDocumentRef}
            handleFileUpload={(event, field, fieldName) => {
              fileUpload({
                event,
                field,
                fieldName,
                formik,
                documentOwner,
                clientId,
                setProofOfAddressImageURL,
                setCpfFileImageURL,
                setRgFileImageURL,
                setSelfieImageURL,
              });
            }}
            apiError={documentsApiError}
          />
          <AdditionalInformationForm formik={formik} formType="guarantor" />
        </div>

        <div className="register-container__buttons-container">
          <AdaButton
            id="button-register-submit"
            type="submit"
            onClick={async () => {
              const error = await formik.validateForm();
              checkErrors({
                errors: error,
                degreeOfRelatednessRef,
                nationalityRef,
                cpfFileRef,
                rgFileRef,
                proofOfAddressRef,
                selfieWithDocumentRef,
              });
            }}
          >
            Cadastrar avalista
          </AdaButton>

          {currentState !== StatesEnum.SCORE_GUARANTOR_REGISTRATION && (
            <AdaButton
              className="register-container__buttons-container--space"
              kind="secondary"
              href="/register/guarantors"
              onClick={() => callTrack('Page Return Intended', 'Voltar')}
            >
              Voltar
            </AdaButton>
          )}
        </div>
      </form>
    </DefaultLayout>
  );
}

export default GuarantorsForm;
